/**
 * Ship-computer alerts (audio-direction §5: "short tonal sequences + text, with alert priorities"): hull low
 * (once per crossing, repeating while it stays low), shield down, incoming lock, wave start and wave cleared.
 * Clean cockpit tones on the UI bus at the alarm priority. Allocation-free.
 */
import { ROUTE, type AudioEngine } from './engine'
import type { CombatSummary } from './router'
import { soundIndex, type AlarmConfig } from './sfxSchema'
import { PRIORITY } from './voices'

export interface AlarmState {
  hullLowArmed: boolean
  hullLowAt: number
  shieldDownAt: number
  incomingAt: number
  /** Alerts played (tests and the lab). */
  played: number
}

export function createAlarmState(): AlarmState {
  return { hullLowArmed: true, hullLowAt: -Infinity, shieldDownAt: -Infinity, incomingAt: -Infinity, played: 0 }
}

const HULL_LOW = soundIndex('alertHullLow')
const SHIELD_DOWN = soundIndex('alertShieldDown')
const INCOMING = soundIndex('alertIncomingLock')
const WAVE_START = soundIndex('alertWaveStart')
const WAVE_CLEARED = soundIndex('alertWaveCleared')
/** Hull must recover this far above the threshold before the alert re-arms (no chatter at the edge). */
const HULL_HYSTERESIS = 0.05

type AlarmSink = Pick<AudioEngine, 'play'>

function alert(state: AlarmState, sink: AlarmSink, sound: number, cfg: AlarmConfig): void {
  sink.play(sound, cfg.gain, ROUTE.ui, PRIORITY.alarm)
  state.played++
}

/**
 * One frame. `hull` is the player's hull fraction (0..1), `alive` whether the player lives, `waveCleared` true
 * on the frame the last raider of a wave is gone.
 */
export function stepAlarms(state: AlarmState, cfg: AlarmConfig, hull: number, alive: boolean, summary: CombatSummary, waveCleared: boolean, now: number, sink: AlarmSink): void {
  if (!alive || summary.playerRespawned) {
    state.hullLowArmed = true
  } else if (hull < cfg.hullLowFraction) {
    if (state.hullLowArmed || now - state.hullLowAt >= cfg.hullLowRepeatSeconds) {
      alert(state, sink, HULL_LOW, cfg)
      state.hullLowAt = now
      state.hullLowArmed = false
    }
  } else if (hull >= cfg.hullLowFraction + HULL_HYSTERESIS) state.hullLowArmed = true

  if (alive && summary.shieldDown && now - state.shieldDownAt >= cfg.shieldDownMinInterval) {
    alert(state, sink, SHIELD_DOWN, cfg)
    state.shieldDownAt = now
  }
  if (alive && summary.incomingLock && now - state.incomingAt >= cfg.incomingLockMinInterval) {
    alert(state, sink, INCOMING, cfg)
    state.incomingAt = now
  }
  if (summary.waveStarted > 0) alert(state, sink, WAVE_START, cfg)
  else if (waveCleared) alert(state, sink, WAVE_CLEARED, cfg)
}
